import { ACCESS_TOKEN_MAX_AGE_MINUTE, DEFAULT_COOKIE_OPTIONS, REFRESH_TOKEN_MAX_AGE_MINUTE } from '@src/consts/cookies';
import { IUser } from '@src/models/auth';
import { AnyType } from '@src/models/common';
import { getTokenMinuteExpires } from '@src/utils/cookies';
import jwtDecode from 'jwt-decode';
import { AuthClient } from '../authClient';
import { getRefreshTokenName } from '../utils';
import { SECOND_DIFF } from './consts';
import { INextRequest, INextResponse } from './models';

/**
 * Класс предназначен для работы в среде Edge @link https://edge-runtime.vercel.app
 * Основной его задачей является проверка авторизации для nextjs проектов в промежуточной слое (middleware)
 */
export class ServerNgApiClient {
  private configUrls: {
    auth: string;
  };

  private accessTokenName: string;

  private refreshTokenName: string;

  /** Объект NextRequest @link https://nextjs.org/docs/api-reference/next/server  */
  private request: INextRequest;

  /** Объект NextResponse @link https://nextjs.org/docs/api-reference/next/server  */
  private response: INextResponse;

  constructor(config: { cookieName: string; auth: string; request: AnyType; response: AnyType }) {
    this.configUrls = {
      auth: config.auth,
    };

    this.accessTokenName = config.cookieName;
    this.refreshTokenName = getRefreshTokenName(config.cookieName);
    this.request = config.request;
    this.response = config.response;
  }

  /**
   * Проверяем токены на валидность
   * В текущей реализации при отсутствии корректных куки редирект на авторизацию не реализован.
   * Предполагается, что данную логику выполнить далее AppProvider в рамках уже проверки на клиенте, который выполнит нужный редирект
   */
  public checkAuth = async () => {
    try {
      if (this.accessTokenName) {
        const token = this.request.cookies.get(this.accessTokenName)?.value;

        if (token) {
          try {
            const user: IUser = jwtDecode(token);

            if (user.exp) {
              /** Дата протухания в токене указана в секундах, учитываем это при сравнении с текущей датой */
              const expUserDate = Number(user.exp);
              const nowDate = Math.trunc(Date.now() / 1000);

              /**
               * Если разница между текущей датой и датой протухания токена будет меньше
               * будет менее этого значения, дальнейшие операции с токен бессмыслены, будем считать что он протух
               */
              if (expUserDate - nowDate > SECOND_DIFF) {
                return this.response;
              } else {
                this.response.cookies.delete(this.accessTokenName);
              }
            }
          } catch (error) {
            this.response.cookies.delete(this.accessTokenName);
          }
        }

        const refreshToken = this.request.cookies.get(this.refreshTokenName)?.value;

        if (refreshToken) {
          try {
            // Т.к. запрос будет выполнять в middleware (среда edge), указываем соответствующий тип
            const authClient = new AuthClient({
              baseUrl: this.configUrls.auth,
              type: 'edge',
            });

            const data = await authClient.authorizing(refreshToken);

            if (data) {
              this.response.cookies.set(this.accessTokenName, data.accessToken, {
                ...DEFAULT_COOKIE_OPTIONS,
                expires: getTokenMinuteExpires(ACCESS_TOKEN_MAX_AGE_MINUTE, true),
              });
              this.response.cookies.set(this.refreshTokenName, data.refreshToken, {
                ...DEFAULT_COOKIE_OPTIONS,
                expires: getTokenMinuteExpires(REFRESH_TOKEN_MAX_AGE_MINUTE, true),
              });

              return this.response;
            }
          } catch (error) {
            this.response.cookies.delete(this.refreshTokenName);
          }
        }
      }

      return this.response;
    } catch (error) {
      return this.response;
    }
  };
}
